/**
 * Phase 2A.7 — Parity Audit Page
 * ───────────────────────────────────────────────────────────────────
 * Admin-only, read-only finance review surface for the Phase 2A
 * Preview Engine. Consumes:
 *   GET /api/admin/allocations/preview/parity-audit  (JSON)
 *   GET /api/admin/allocations/preview/parity-audit.csv  (CSV download)
 *
 * Strictly READ-ONLY — no buttons that mutate any production data.
 * Triple-gated: RBAC `allocation.preview` + backend feature flag + the
 * admin layout's existing route protection.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Download, RefreshCcw, Info, AlertTriangle, CheckCircle2, ShieldCheck, ShieldAlert, ShieldX, FileSignature, X as XIcon } from 'lucide-react';

const STATUS_STYLES = {
  MATCH: 'border-emerald-300 text-emerald-700 bg-emerald-50',
  MINOR_VARIANCE: 'border-amber-300 text-amber-700 bg-amber-50',
  MATERIAL_DIFFERENCE: 'border-red-300 text-red-700 bg-red-50',
};

// Phase 2A.8 — Phase 2B Readiness Gate tolerance.
// MINOR_VARIANCE rows are tolerated up to this ratio of MATCH rows.
// Conservative default: 5% of clean rows. Pure UI threshold — no
// backend dependency.
export const READINESS_MINOR_TOLERANCE_RATIO = 0.05;

/**
 * Pure readiness derivation. Exported for testability.
 * @param {{ MATCH: number, MINOR_VARIANCE: number, MATERIAL_DIFFERENCE: number }} counts
 * @returns {{ state: 'READY'|'WARNING'|'BLOCKED', reason: string, tolerance: number }}
 */
export function deriveReadiness(counts) {
  const match = Number(counts?.MATCH || 0);
  const minor = Number(counts?.MINOR_VARIANCE || 0);
  const material = Number(counts?.MATERIAL_DIFFERENCE || 0);
  const tolerance = Math.max(0, Math.floor(match * READINESS_MINOR_TOLERANCE_RATIO));
  if (material > 0) {
    return {
      state: 'BLOCKED',
      reason: `${material} shift${material === 1 ? '' : 's'} flagged MATERIAL_DIFFERENCE. Resolve all material variances before Phase 2B sign-off.`,
      tolerance,
    };
  }
  if (minor > tolerance) {
    return {
      state: 'WARNING',
      reason: `${minor} MINOR_VARIANCE row${minor === 1 ? '' : 's'} exceeds the tolerance of ${tolerance} (${Math.round(READINESS_MINOR_TOLERANCE_RATIO * 100)}% of MATCH rows). Review and reduce variances before sign-off.`,
      tolerance,
    };
  }
  return {
    state: 'READY',
    reason:
      material === 0 && minor === 0
        ? 'All audited shifts match the legacy invoice within INFO tolerance. Tenant is ready for Phase 2B sign-off.'
        : `${minor} MINOR_VARIANCE row${minor === 1 ? '' : 's'} within the tolerance of ${tolerance}. Tenant is ready for Phase 2B sign-off.`,
    tolerance,
  };
}

export default function ParityAuditPage() {
  const { hasPermission } = useAuth();
  const allowed = hasPermission('allocation.preview');
  const canDraft = hasPermission('allocation.draft_fanout');

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [severity, setSeverity] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Phase 2B — local draft action state
  const [drafting, setDrafting] = useState({}); // { [shift_id]: 'generating' | 'done' | 'error' }
  const [draftMsg, setDraftMsg] = useState(null);

  const fetchAudit = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (severity) params.set('severity', severity);
      const r = await api.get(`/admin/allocations/preview/parity-audit?${params.toString()}`);
      setResult(r.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || 'Failed to load parity audit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (allowed) fetchAudit(); /* eslint-disable-next-line */ }, []);

  // Phase 2B — generate a draft fan-out for a single shift (admin-only).
  // Server-side enforces readiness + flag + permission gates; this UI is
  // strictly a trigger that displays the response message inline.
  const generateDraft = async (shiftId) => {
    setDrafting((s) => ({ ...s, [shiftId]: 'generating' }));
    setDraftMsg(null);
    try {
      const r = await api.post('/admin/allocations/draft-fanout/generate', { shift_id: shiftId });
      const d = r.data?.data || {};
      if (d.feature_enabled === false) {
        setDraftMsg({ kind: 'warn', text: 'Phase 2B draft fan-out is OFF for this tenant.' });
        setDrafting((s) => ({ ...s, [shiftId]: 'error' }));
        return;
      }
      const idem = r.data?.idempotent ? ' (idempotent — existing draft returned)' : '';
      setDraftMsg({ kind: 'ok', text: `Draft ${d.batch_id} generated${idem}.` });
      setDrafting((s) => ({ ...s, [shiftId]: 'done' }));
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
                  || e?.response?.data?.detail?.code
                  || e?.response?.data?.error
                  || 'Draft generation failed';
      setDraftMsg({ kind: 'err', text: String(msg) });
      setDrafting((s) => ({ ...s, [shiftId]: 'error' }));
    }
  };

  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (severity) params.set('severity', severity);
    const base = process.env.REACT_APP_BACKEND_URL || '';
    // Use the api helper's underlying auth interceptor pattern — but `<a>` won't
    // carry Authorization headers. Instead we hit the JSON endpoint via api and
    // build a Blob client-side from the same data the user just saw.
    api
      .get(`/admin/allocations/preview/parity-audit.csv?${params.toString()}`, {
        responseType: 'blob',
      })
      .then((r) => {
        const blob = new Blob([r.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'parity_audit.csv';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('CSV download failed'));
    void base; // suppress unused warning
  };

  if (!allowed) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Parity Audit Report</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-600">
            You do not have the <code>allocation.preview</code> permission required to view this report.
          </CardContent>
        </Card>
      </div>
    );
  }

  const featureOff = result && result.feature_enabled === false;

  return (
    <div className="p-6 space-y-4" data-testid="parity-audit-page">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Parity Audit Report
              <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">
                Read-only · Phase 2A.7
              </Badge>
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={fetchAudit}
                disabled={loading}
                data-testid="parity-audit-refresh"
              >
                <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
              <Button
                variant="default" size="sm"
                onClick={downloadCsv}
                disabled={loading || featureOff}
                data-testid="parity-audit-download-csv"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          <div className="rounded-md border bg-blue-50 border-blue-200 px-3 py-2 text-xs text-blue-900 flex gap-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              This report is a <strong>projection only</strong>. It compares the
              current single-line invoice total to the projected multi-service
              fan-out total per shift. Invoices, claims, payroll, and funding
              ledgers are NOT modified by this view.
            </span>
          </div>

          {result && result.feature_enabled && (() => {
            const readiness = deriveReadiness(result.counts || {});
            const meta = {
              READY: {
                Icon: ShieldCheck,
                cls: 'border-emerald-300 bg-emerald-50 text-emerald-800',
                badgeCls: 'border-emerald-400 text-emerald-700 bg-white',
                label: 'READY FOR PHASE 2B',
              },
              WARNING: {
                Icon: ShieldAlert,
                cls: 'border-amber-300 bg-amber-50 text-amber-900',
                badgeCls: 'border-amber-400 text-amber-700 bg-white',
                label: 'WARNING — NOT YET READY',
              },
              BLOCKED: {
                Icon: ShieldX,
                cls: 'border-red-300 bg-red-50 text-red-800',
                badgeCls: 'border-red-400 text-red-700 bg-white',
                label: 'BLOCKED — NOT READY FOR PHASE 2B',
              },
            }[readiness.state];
            const Icon = meta.Icon;
            return (
              <div
                className={`rounded-md border px-3 py-2.5 flex gap-3 items-start ${meta.cls}`}
                data-testid="readiness-gate"
                data-state={readiness.state}
              >
                <Icon className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm">
                      <span data-testid="readiness-gate-label">{meta.label}</span>
                    </div>
                    <div className="flex gap-1">
                      <Badge variant="outline" className={`text-[10px] ${meta.badgeCls}`} data-testid="readiness-gate-match">
                        MATCH {result.counts?.MATCH ?? 0}
                      </Badge>
                      <Badge variant="outline" className={`text-[10px] ${meta.badgeCls}`} data-testid="readiness-gate-minor">
                        MINOR {result.counts?.MINOR_VARIANCE ?? 0}
                      </Badge>
                      <Badge variant="outline" className={`text-[10px] ${meta.badgeCls}`} data-testid="readiness-gate-material">
                        MATERIAL {result.counts?.MATERIAL_DIFFERENCE ?? 0}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-xs" data-testid="readiness-gate-reason">{readiness.reason}</div>
                  <div className="text-[10px] opacity-75" data-testid="readiness-gate-tolerance">
                    Tolerance: up to {readiness.tolerance} MINOR_VARIANCE row(s) ({Math.round(READINESS_MINOR_TOLERANCE_RATIO * 100)}% of MATCH).
                    Read-only governance signal — no draft invoices or financial records are created by this widget.
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label htmlFor="parity-audit-date-from">Date from</Label>
              <Input
                id="parity-audit-date-from" type="date" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="parity-audit-date-from"
              />
            </div>
            <div>
              <Label htmlFor="parity-audit-date-to">Date to</Label>
              <Input
                id="parity-audit-date-to" type="date" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="parity-audit-date-to"
              />
            </div>
            <div>
              <Label htmlFor="parity-audit-severity">Severity</Label>
              <select
                id="parity-audit-severity" value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full border rounded-md h-9 px-2 text-sm bg-white"
                data-testid="parity-audit-severity"
              >
                <option value="">All</option>
                <option value="MATCH">MATCH</option>
                <option value="MINOR_VARIANCE">MINOR_VARIANCE</option>
                <option value="MATERIAL_DIFFERENCE">MATERIAL_DIFFERENCE</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchAudit} disabled={loading} className="w-full" data-testid="parity-audit-apply">
                Apply filters
              </Button>
            </div>
          </div>

          {error && (
            <div className="rounded border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="parity-audit-error">
              {error}
            </div>
          )}

          {featureOff && (
            <div className="rounded border border-amber-300 bg-amber-50 text-amber-800 text-sm px-3 py-2 flex gap-2" data-testid="parity-audit-feature-off">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <span>
                The Phase 2A preview engine is currently <strong>OFF</strong>.
                Enable the umbrella + preview feature flags in production
                settings to populate this report.
              </span>
            </div>
          )}

          {result && result.feature_enabled && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="parity-audit-counts">
                <Stat label="MATCH" value={result.counts?.MATCH ?? 0} status="MATCH" testid="parity-audit-count-match" />
                <Stat label="MINOR_VARIANCE" value={result.counts?.MINOR_VARIANCE ?? 0} status="MINOR_VARIANCE" testid="parity-audit-count-minor" />
                <Stat label="MATERIAL_DIFFERENCE" value={result.counts?.MATERIAL_DIFFERENCE ?? 0} status="MATERIAL_DIFFERENCE" testid="parity-audit-count-material" />
                <Stat label="Rows reported" value={result.reported_row_count ?? 0} testid="parity-audit-count-rows" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <Total label="Legacy total" value={result.totals?.legacy_total} />
                <Total label="Projected total" value={result.totals?.projected_total} />
                <Total label="Absolute Δ total" value={result.totals?.absolute_delta_total} />
              </div>

              <div className="border rounded-md overflow-x-auto" data-testid="parity-audit-table-wrap">
                {draftMsg && (
                  <div
                    className={`px-3 py-1.5 text-xs flex items-center justify-between gap-2 border-b ${
                      draftMsg.kind === 'ok' ? 'bg-emerald-50 text-emerald-700' :
                      draftMsg.kind === 'warn' ? 'bg-amber-50 text-amber-800' :
                      'bg-red-50 text-red-700'
                    }`}
                    data-testid="parity-audit-draft-msg"
                  >
                    <span>{draftMsg.text}</span>
                    <button onClick={() => setDraftMsg(null)} className="text-current opacity-60 hover:opacity-100">
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-2 py-1.5">Shift</th>
                      <th className="text-left px-2 py-1.5">Date</th>
                      <th className="text-right px-2 py-1.5">Activities</th>
                      <th className="text-right px-2 py-1.5">Legacy</th>
                      <th className="text-right px-2 py-1.5">Projected</th>
                      <th className="text-right px-2 py-1.5">Δ</th>
                      <th className="text-right px-2 py-1.5">Δ %</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                      {canDraft && <th className="text-left px-2 py-1.5">Action</th>}
                    </tr>
                  </thead>
                  <tbody data-testid="parity-audit-tbody">
                    {(result.rows || []).map((row) => (
                      <tr key={row.shift_id} className="border-t" data-testid={`parity-audit-row-${row.shift_id}`}>
                        <td className="px-2 py-1 font-mono text-[10px]">{row.shift_id.slice(-8)}</td>
                        <td className="px-2 py-1">{row.shift_date || '—'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{row.activity_count}</td>
                        <td className="px-2 py-1 text-right tabular-nums">${Number(row.legacy_invoice_amount || 0).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">${Number(row.projected_amount || 0).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">${Number(row.amount_delta || 0).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{row.amount_delta_pct != null ? `${row.amount_delta_pct}%` : '—'}</td>
                        <td className="px-2 py-1">
                          <Badge variant="outline" className={`text-[9px] ${STATUS_STYLES[row.parity_status] || ''}`}>
                            {row.parity_status === 'MATCH' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                            {row.parity_status === 'MATERIAL_DIFFERENCE' && <AlertTriangle className="w-3 h-3 mr-1" />}
                            {row.parity_status}
                          </Badge>
                        </td>
                        {canDraft && (
                          <td className="px-2 py-1">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={row.parity_status !== 'MATCH' || drafting[row.shift_id] === 'generating'}
                              onClick={() => generateDraft(row.shift_id)}
                              data-testid={`parity-audit-generate-draft-${row.shift_id}`}
                              className="h-6 px-2 text-[10px]"
                              title={row.parity_status !== 'MATCH'
                                ? 'Drafts can only be generated for MATCH rows'
                                : 'Generate a non-submittable draft fan-out'}
                            >
                              <FileSignature className="w-3 h-3 mr-1" />
                              {drafting[row.shift_id] === 'generating' ? 'Generating…' :
                               drafting[row.shift_id] === 'done' ? 'Generated ✓' :
                               'Generate draft'}
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {(result.rows || []).length === 0 && (
                      <tr>
                        <td colSpan={canDraft ? 9 : 8} className="text-center text-slate-500 italic py-4" data-testid="parity-audit-empty">
                          No shifts match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const Stat = ({ label, value, status, testid }) => (
  <div className={`rounded border px-2.5 py-1.5 ${status ? STATUS_STYLES[status] : 'border-slate-200 bg-white'}`} data-testid={testid}>
    <div className="text-[9px] uppercase tracking-wide opacity-75">{label}</div>
    <div className="text-lg font-semibold tabular-nums">{value}</div>
  </div>
);

const Total = ({ label, value }) => (
  <div className="rounded border border-slate-200 bg-white px-2.5 py-1.5">
    <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-base font-semibold tabular-nums text-slate-800">${Number(value || 0).toFixed(2)}</div>
  </div>
);
